import React, { useState, useRef } from "react";
import Modal from "react-modal";
import { X, Video, Save, AlertCircle, Info } from "react-feather";
import { Loader } from "../../../../Components/Loader/index";
import { toggleRecordMode } from "../../../../redux/actions/resultsActions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "./AnimationRecord.scss";
import html2canvas from "html2canvas";
import { primaryColors } from "../../../../common/Colors";

const AnimationRecord = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const animRef = useRef(null);

  const [isLegendIncluded, includeLegend] = useState(false);
  const [isBackgroundHidden, includeBackground] = useState(false);

  const [isRecording, setRecording] = useState(false);
  const [isFinished, setFinished] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);
  const [hasAnimation, checkAnimation] = useState(true);
  const [animationDuration, setAnimationDuration] = useState(5);

  const minDuration = 5;
  const maxDuration = 20;

  const getZIndex = (element) => {
    const zIndex = window.getComputedStyle(element).zIndex;
    return zIndex === "auto" ? 0 : parseInt(zIndex);
  };

  const captureBackground = () => {
    return new Promise((resolve) => {
      const mapContainer = document.querySelector(".leaflet-container");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = mapContainer.clientWidth;
      canvas.height = mapContainer.clientHeight;

      const imageLayers = [
        ...mapContainer.querySelectorAll(".leaflet-image-layer"),
      ];
      const tileLayers = [
        ...mapContainer.querySelectorAll(".leaflet-tile-loaded"),
      ];
      const windDirectionCanvas = document.querySelector(".wind-direction");

      imageLayers.sort((a, b) => getZIndex(a) - getZIndex(b));

      const loadImage = (src, layer) => {
        return new Promise((resolveImage) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = src;

          img.onload = () => {
            const { x, y, width, height } = layer.getBoundingClientRect();
            const mapRect = mapContainer.getBoundingClientRect();
            const offsetX = x - mapRect.left;
            const offsetY = y - mapRect.top;
            ctx.drawImage(img, offsetX, offsetY, width, height);
            resolveImage();
          };

          img.onerror = () => {
            console.error(`🚨 Failed to load image: ${src}`);
            resolveImage();
          };
        });
      };

      let tilePromises = [];

      if (!isBackgroundHidden) {
        tilePromises = tileLayers.map((tile) => loadImage(tile.src, tile));
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      setTimeout(async () => {
        const imagePromises = imageLayers.map((imgLayer) =>
          loadImage(imgLayer.src, imgLayer)
        );
        //console.log(tilePromises);
        await Promise.all([...tilePromises, ...imagePromises]);

        // ✅ Draw wind direction canvas if available
        if (windDirectionCanvas) {
          const { x, y, width, height } =
            windDirectionCanvas.getBoundingClientRect();
          const mapRect = mapContainer.getBoundingClientRect();
          const offsetX = x - mapRect.left;
          const offsetY = y - mapRect.top;
          ctx.drawImage(windDirectionCanvas, offsetX, offsetY, width, height);
        }

        // ✅ Capture and draw legends
        if (isLegendIncluded) {
          const legendDivs = [...document.querySelectorAll(".res-geo-legend")];
          const legendCanvases = await Promise.all(
            legendDivs.map((div) => html2canvas(div, { backgroundColor: null }))
          );

          legendCanvases.forEach((legendCanvas, index) => {
            const div = legendDivs[index];
            const { x, y, width, height } = div.getBoundingClientRect();
            const mapRect = mapContainer.getBoundingClientRect();
            const offsetX = x - mapRect.left;
            const offsetY = y - mapRect.top;
            ctx.drawImage(legendCanvas, offsetX, offsetY, width, height);
          });
        }

        // ✅ Finalize and return image
        resolve(canvas.toDataURL("image/png"));
      }, 100);
    });
  };

  const recordAnimation = async (background) => {
    const animationCanvas = document.querySelector(".wind-animation");
    if (!animationCanvas) return;

    setRecording(true);
    setFinished(false);

    const canvas = props.canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = animationCanvas.width;
    canvas.height = animationCanvas.height;

    const bgImage = new Image();
    bgImage.src = background;

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        animationCanvas,
        0,
        0,
        animationCanvas.width,
        animationCanvas.height
      );

      if (!isRecording) {
        requestAnimationFrame(animate);
      }
    }

    // for (let i = 0; i < 4; i++) {
    //   animate();
    // }
    bgImage.onload = () => {
      animate(); // Start animation only after background is loaded
    };
    const isFirefox = /firefox/i.test(navigator.userAgent);

    let mimeType = "video/webm; codecs=vp9";
    let recordDelay = 4000;
    if (isFirefox) {
      mimeType = "video/webm; codecs=vp8";
      recordDelay = 3000;
    }

    const options = {
      mimeType: mimeType,
      videoBitsPerSecond: 10_000_000,
    };

    const chunks = [];
    const stream = canvas.captureStream(60);
    const rec = new MediaRecorder(stream, options);

    rec.ondataavailable = (e) => chunks.push(e.data);
    rec.onstop = () => saveVideo(new Blob(chunks, { type: "video/webm" }));

    setTimeout(() => {
      rec.start();
    }, 3000);

    setTimeout(
      () => rec.stop(),
      Number(animationDuration) * 1000 + recordDelay
    );
  };

  const saveVideo = (blob) => {
    const vid = document.createElement("video");
    vid.src = URL.createObjectURL(blob);
    vid.controls = true;
    vid.className = "animation-video";

    if (animRef.current) {
      vid.style.width = "100%";
      vid.style.border = "1px solid rgb(187, 187, 187)";
      vid.style.height = "55vh";
      animRef.current.appendChild(vid);
    }

    setRecording(false);
    setFinished(true);
    setDownloadLink(vid.src);
  };

  const handleDownload = () => {
    if (downloadLink) {
      const link = document.createElement("a");
      link.href = downloadLink;
      link.download = "animation.webm";
      link.click();
    }
  };

  const startRecording = async () => {
    const animationCanvas = document.querySelector(".wind-animation");
    if (!animationCanvas) {
      checkAnimation(false);
      return;
    }
    setRecording(true);
    setFinished(false);
    checkAnimation(true);

    const prevVideo = document.querySelector(".animation-video");
    if (prevVideo) prevVideo.remove();

    const background = await captureBackground();
    recordAnimation(background);
  };

  const handleAnimationDurationChange = (event) => {
    setAnimationDuration(event.target.value);
  };

  const calculateLeft = () => {
    const percentage =
      ((Number(animationDuration) - minDuration) /
        (maxDuration - minDuration)) *
      100;
    const offset =
      Number(animationDuration) === minDuration
        ? 0
        : Number(animationDuration) === maxDuration
        ? 20
        : 20;

    return `calc(${percentage}% - ${percentage * 0.24}px`;
  };

  const onIncludeBackgroundChange = () => {
    includeBackground((prev) => !prev);
  };

  const onIncludeLegendChange = () => {
    includeLegend((prev) => !prev);
  };

  return (
    <Modal style={modalCustomStyle} isOpen={props.isOpen}>
      <div className="modal-wrapper">
        <div className="modal-hdr-wrapper">
          <div className="modal-hdr" style={{ fontSize: "17px" }}>
            Record Menu
          </div>
          <X
            onClick={() => dispatch(toggleRecordMode())}
            size={"20px"}
            style={{ cursor: "pointer" }}
            className="exit-modal"
          />
        </div>

        {!hasAnimation && (
          <div
            className="sim-warning"
            style={{
              marginBottom: "28px",
              padding: "12px",
              fontSize: "13px",
              display: "flex",
            }}
          >
            <div
              style={{
                minWidth: 14,
                display: "flex",
                marginRight: "6px",
                alignItems: "center",
              }}
            >
              <AlertCircle size="14" stroke="#a81d1dcf" strokeWidth="1.6" />
            </div>
            There is no animation displayed on map. First activate it on map.
          </div>
        )}
        <div
          style={{ display: "flex", marginBottom: "7px", marginTop: "17px" }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            Recording Time [s]
          </div>
          <div
            style={{
              width: "200px",
              textAlign: "center",
              position: "relative",
              marginLeft: "12px",
            }}
          >
            <div className="range">
              <div
                style={{
                  position: "absolute",
                  bottom: "36px",
                  left: calculateLeft(),
                  backgroundColor: primaryColors.primary,
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "11px",
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  marginTop: "-2px",
                  paddingBottom: "6px",
                }}
              >
                {animationDuration}
              </div>
              <input
                type="range"
                min={minDuration}
                max={maxDuration}
                value={animationDuration}
                onChange={handleAnimationDurationChange}
                style={{
                  fontSize: "11px",
                  width: "100%",
                  margin: "0px",
                  background: primaryColors.primary,
                }}
              />
            </div>
          </div>
        </div>

        <label
          className="legend-row-container legend-wrapper"
          style={{ marginTop: "4px" }}
        >
          <input
            className="raster-checkbox"
            name={"invert-color"}
            type="checkbox"
            checked={isBackgroundHidden}
            onChange={onIncludeBackgroundChange}
          />
          <label className="raster-object-name">{t("Hide Background")}</label>
        </label>
        <label
          className="legend-row-container legend-wrapper"
          style={{ marginTop: "12px" }}
        >
          <input
            className="raster-checkbox"
            name={"invert-color"}
            type="checkbox"
            checked={isLegendIncluded}
            onChange={onIncludeLegendChange}
          />
          <label className="raster-object-name">{t("Add Legends")}</label>
        </label>
        <div
          className="raster-object-name"
          style={{ fontSize: "11px", fontWeight: 400, marginBottom: "16px" }}
        >
          {t(
            "* Including legends in animation will decrease animation performance"
          )}
        </div>
        {isRecording && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "46px",
              marginTop: "33px",
              textAlign: "center",
              flexDirection: "column",
            }}
          >
            <Loader size="big" />
            <div
              style={{ marginTop: "14px", fontSize: "13px", fontWeight: 600 }}
            >
              Recording...
            </div>
          </div>
        )}

        <div ref={animRef} className="video-container"></div>

        <div className="modal-btn-wrapper" style={{ marginTop: "8px" }}>
          <button
            className="btn-modal-cancel"
            onClick={handleDownload}
            disabled={!isFinished}
            style={{
              display: "flex",
              height: "fit-content",
              alignItems: "center",
              marginTop: "12px",
              padding: "7px 4px 7px 4px",
              minWidth: "100px",
              justifyContent: "space-evenly",
            }}
          >
            <div style={{ display: "flex" }}>
              <Save size={"14px"} strokeWidth={"1.8px"} />
            </div>
            {t(`Download`)}
          </button>
          <button
            className="btn-modal-delete"
            onClick={startRecording}
            disabled={isRecording}
            style={{
              display: "flex",
              height: "fit-content",
              alignItems: "center",
              marginTop: "12px",
              padding: "7px 4px 7px 4px",
              minWidth: "100px",
              justifyContent: "space-evenly",
            }}
          >
            <div style={{ display: "flex" }}>
              <Video size={"14px"} strokeWidth={"2.2px"} />
            </div>
            <div style={{ marginRight: "7px" }}>{t(`Record`)}</div>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AnimationRecord;

const modalCustomStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "5px",
    padding: "30px 38px 28px",
    border: "1px solid rgb(187 187 187)",
    maxHeight: "90%",
    minWidth: "600px",
    maxWidth: "75%",
  },
  overlay: {
    backdropFilter: "blur(8px)",
    zIndex: 1000,
  },
};
